import { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { withArabicDigits, formatDateYYYYMMDD } from '../../../utils/numbers';

const HeroSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await import('../../../services/articlesService').then(m => m.default.getFeaturedArticles());
        setFeaturedArticles(data || []);
      } catch (error) {
        console.error('Failed to fetch featured articles', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredArticles?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [featuredArticles?.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredArticles?.length) % featuredArticles?.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredArticles?.length);
  };

  const currentArticle = featuredArticles?.[currentSlide];

  if (loading) return null;
  if (!featuredArticles?.length) return null;

  return (
    <section className="relative w-full h-auto lg:h-[700px] overflow-hidden" style={{ backgroundColor: '#F9F7F2', zIndex: 1 }}>
      {/* Mobile Blurry Background Layer - Only visible on mobile */}
      <div
        className="absolute inset-0 w-full h-full z-0 lg:hidden bg-cover bg-center bg-no-repeat blur-3xl opacity-[0.45]"
        style={{ backgroundImage: `url(${currentArticle?.image})` }} />


      <div className="absolute inset-0 hidden lg:block">
        <Image
          src={currentArticle?.image}
          alt={currentArticle?.imageAlt}
          className="w-full h-full object-cover opacity-20" />

        <div className="absolute inset-0 bg-gradient-to-t from-[#F9F7F2] via-transparent to-transparent" />
      </div>
      <div className="relative h-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 lg:py-0 lg:flex lg:items-center mb-[46px]">
        <div className="w-full flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Visual - First on Mobile, resized circles */}
          <div className="flex items-center justify-center order-1 lg:order-2">
            <div className="relative w-48 h-48 lg:w-96 lg:h-96" style={{ backgroundColor: 'transparent', border: 'none', outline: 'none' }}>
              <Image
                src={currentArticle?.image}
                alt={currentArticle?.imageAlt}
                className="w-full h-full object-cover rounded-full"
                style={{ border: 'none', outline: 'none' }} />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-full" style={{ border: 'none' }} />
            </div>
          </div>

          {/* Text Content - Second on Mobile */}
          <div className="flex flex-col justify-center space-y-4 md:space-y-6 text-center lg:text-right order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-[var(--color-accent)]/10 px-4 py-2 rounded-full w-fit mx-auto lg:mx-0" style={{ border: 'none', outline: 'none' }}>
              <Icon name="Circle" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-[var(--color-accent)]" style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}>
                {currentArticle?.circle}
              </span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold text-[var(--color-foreground)] leading-tight" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
              {currentArticle?.title}
            </h1>

            <p className="text-base lg:text-xl text-[var(--color-muted-foreground)] leading-relaxed line-clamp-3" style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}>
              {currentArticle?.excerpt}
            </p>

            <div className="flex items-center gap-4 justify-center lg:justify-start">
              <Image
                src={currentArticle?.authorImage}
                alt={currentArticle?.authorImageAlt}
                className="w-12 h-12 rounded-full object-cover" />

              <div className="flex flex-col text-center lg:text-right">
                <span className="text-base font-medium text-[var(--color-foreground)]" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
                  {currentArticle?.author}
                </span>
                <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}>
                  <span>{withArabicDigits(currentArticle?.readTime ?? '')}</span>
                  <span>•</span>
                  <span dir="ltr">{formatDateYYYYMMDD(currentArticle?.publishedDate ?? '')}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 justify-center lg:justify-start flex-wrap mt-0.5 mb-[5px] pb-[7px]">
              <Button
                variant="default"
                size="lg"
                iconName="BookOpen"
                iconPosition="right"
                className="rounded-full gap-3"
                onClick={() => navigate(`/article/${currentArticle?.id}`)}
                style={{
                  fontFamily: 'Amiri, serif',
                  fontWeight: 400,
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  outline: 'none'
                }}>
                اقرأ المقال
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 mt-[9px] py-0 mb-[-17px]">
        <button
          onClick={handlePrevSlide}
          className="w-12 h-12 rounded-full bg-white hover:bg-[var(--color-muted)] transition-colors flex items-center justify-center"
          style={{ border: 'none', outline: 'none' }}
          aria-label="المقال السابق">
          <Icon name="ChevronRight" size={20} color="var(--color-foreground)" />
        </button>

        <div className="flex items-center gap-2">
          {featuredArticles?.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ?
                'w-8 bg-[var(--color-accent)]' :
                'bg-[var(--color-muted-foreground)]/30'}`
              }
              aria-label={`انتقل إلى المقال ${index + 1}`} />
          )}
        </div>

        <button
          onClick={handleNextSlide}
          className="w-12 h-12 rounded-full bg-white hover:bg-[var(--color-muted)] transition-colors flex items-center justify-center"
          style={{ border: 'none', outline: 'none' }}
          aria-label="المقال التالي">
          <Icon name="ChevronLeft" size={20} color="var(--color-foreground)" />
        </button>
      </div>
    </section>);

};

export default HeroSection;